// What the room calls the things the snapshot carries, in the page's
// language: devices, colours and the built-in games' lines
// (src/core/i18n.ts). The snapshot holds the English names, which the
// server and the command line use.

import { deviceName as nameIn, deviceShort as shortIn, phraseRuns, skinName as skinIn, starterTagline, type MessageKey, type Params } from "../core/i18n.ts";
import type { AppInfo, DeviceId, SkinInfo } from "../core/types.ts";
import { lang, t } from "./lang.ts";

/** A device's name: `PSP`, `Nintendo 3DS` (`ニンテンドー3DS`). */
export function deviceName(id: DeviceId): string {
  return nameIn(id, lang());
}

/** What a short sentence calls a device: `3DS` for the Nintendo 3DS. */
export function deviceShort(id: DeviceId): string {
  return shortIn(id, lang());
}

/** A colour's name, by the skin's id; a colour the catalogs do not know keeps its own name. */
export function skinName(device: DeviceId, skin: Pick<SkinInfo, "id" | "name">): string {
  return skinIn(device, skin.id, lang(), skin.name);
}

/** A swatch's label: its name, and for a colour the account does not have, that it is for members. */
export function skinLabel(device: DeviceId, skin: Pick<SkinInfo, "id" | "name" | "owned">): string {
  const name = skinName(device, skin);
  return skin.owned ? name : t("room.colour.locked", { colour: name });
}

/** A game's line: a built-in game's in the page's language, anyone else's as its author wrote it. */
export function appTagline(app: Pick<AppInfo, "id" | "origin" | "tagline">): string {
  return (app.origin === "starter" ? starterTagline(app.id, lang()) : null) ?? app.tagline;
}

/**
 * A sentence with elements in it: `key`'s text split at each `{name}` that
 * `nodes` holds, the node put in its place. Other placeholders are filled
 * from `params`. The order of the parts is the language's.
 */
export function rich(key: MessageKey, nodes: Record<string, Node>, params?: Params): Array<string | Node> {
  const text = t(key, params);
  const parts: Array<string | Node> = [];
  let at = 0;
  for (const match of text.matchAll(/\{(\w+)\}/g)) {
    const node = nodes[match[1]!];
    if (!node) continue;
    if (match.index! > at) parts.push(text.slice(at, match.index));
    parts.push(node);
    at = match.index! + match[0].length;
  }
  if (at < text.length) parts.push(text.slice(at));
  return parts;
}

/**
 * A sentence as nodes in the page's language: in Japanese each number kept
 * with its counter and each device name in a span that does not break
 * (phraseRuns); in English the sentence as it is.
 */
export function phrase(text: string): Array<string | Node> {
  return phraseRuns(lang(), text).map((run) => {
    if (!run.name) return run.text;
    const name = document.createElement("span");
    name.className = "nb";
    name.textContent = run.text;
    return name;
  });
}
